/**
 * Outbound link helpers.
 *
 * We render the RAW merchant URL (or Impact tracking URL) in the <a href>
 * so the AWIN Publisher Master Tag can intercept and attribute the click
 * at the browser level. A `navigator.sendBeacon` fires in parallel to log
 * the click for our internal analytics — async, fire-and-forget, doesn't
 * block navigation.
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Pick the best outbound URL for a curated item / registry item.
 * - Impact items already have a tracking URL stored as base_url (from sync)
 * - AWIN items have raw merchant URL → Master Tag wraps on click
 * - Other items pass through cleanly
 */
export function outboundUrl(item) {
  if (!item) return "";
  return item.url || item.base_url || "";
}

/**
 * Fire a click beacon to our backend for internal analytics.
 * Does NOT block the user's outbound navigation.
 *
 * @param {Object} args
 * @param {"curated"|"item"} args.kind
 * @param {string} args.itemId
 * @param {string} [args.registrySlug]
 * @param {string} [args.destination]  Optional outbound URL (host extracted)
 */
export function trackOutboundClick({ kind, itemId, registrySlug, destination }) {
  if (!itemId || !kind) return;
  const url = `${BACKEND}/api/clicks/log`;
  let host = "";
  try { host = destination ? new URL(destination).host : ""; } catch { host = ""; }
  const payload = {
    kind,
    item_id: itemId,
    registry_slug: registrySlug || null,
    destination_host: host,
  };
  const body = JSON.stringify(payload);
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(url, blob);
      return;
    }
  } catch (err) {
    // fall through to fetch fallback
  }
  // Fallback: fire-and-forget fetch with keepalive so it survives page nav
  try {
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
      credentials: "omit",
    }).catch(() => {});  // swallow network errors — click tracking is non-critical
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("Click beacon fetch failed:", err?.message);
  }
}
